import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react';

export const skeletonAnimations = [
  'pulse',
  'shimmer',
  'shimmer-soft',
  'shimmer-diagonal',
  'shimmer-vertical',
  'drift',
  'outline',
  'none',
] as const;
export type SkeletonAnimation = (typeof skeletonAnimations)[number];
export type SkeletonGroupAnimation = SkeletonAnimation | 'shimmer-shared' | 'shimmer-stagger';

export function validateSkeletonAnimation(value: unknown, grouped = false): void {
  if (value === undefined || skeletonAnimations.includes(value as SkeletonAnimation)) return;
  if (grouped && (value === 'shimmer-shared' || value === 'shimmer-stagger')) return;
  throw new TypeError(
    `Skeleton animation must be ${skeletonAnimations.join(', ')}${grouped ? ', shimmer-shared, or shimmer-stagger' : ''}.`,
  );
}

type Registration = { item?: number };
type Registry = {
  nodes: Map<HTMLElement, Registration>;
  update: () => void;
};
type MotionContextValue = {
  animation: SkeletonGroupAnimation;
  disabled: boolean;
  registry: Registry;
};
const MotionContext = createContext<MotionContextValue | null>(null);
const ItemContext = createContext<number | undefined>(undefined);

function clearGeometry(node: HTMLElement) {
  delete node.dataset.skeletonReady;
  for (const name of ['width', 'offset', 'delay'])
    node.style.removeProperty(`--monkeys-skeleton-${name}`);
}

function useGroupRegistry(
  animation: SkeletonGroupAnimation,
  disabled: boolean,
  enabled: boolean,
  containerRef?: RefObject<HTMLElement>,
) {
  const registry = useMemo<Registry>(() => ({ nodes: new Map(), update: () => {} }), []);
  useEffect(() => {
    if (!enabled || disabled || (animation !== 'shimmer-shared' && animation !== 'shimmer-stagger'))
      return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    let observer: ResizeObserver | undefined;
    let frame: number | undefined;
    let active = false;
    const observed = new Set<HTMLElement>();
    const measure = () => {
      frame = undefined;
      if (!active) return;
      const nodes = [...registry.nodes.keys()].filter((node) => node.isConnected);
      nodes.sort((a, b) => (a.compareDocumentPosition(b) & 2 ? 1 : -1));
      if (animation === 'shimmer-stagger') {
        nodes.forEach((node, index) => {
          const item = registry.nodes.get(node)?.item ?? index;
          node.style.setProperty('--monkeys-skeleton-delay', `${Math.min(item * 80, 400)}ms`);
        });
        return;
      }
      // Container-only resizing can move fixed-size shapes (for example space-between).
      let container = containerRef?.current ?? nodes[0]?.parentElement;
      for (const node of nodes)
        while (container && !container.contains(node)) container = container.parentElement;
      const targets = new Set<HTMLElement>(nodes);
      for (const node of nodes) {
        for (let ancestor = node.parentElement; ancestor; ancestor = ancestor.parentElement) {
          targets.add(ancestor);
          if (ancestor === container) break;
        }
      }
      if (container) targets.add(container);
      for (const node of observed)
        if (!targets.has(node)) {
          observer?.unobserve(node);
          observed.delete(node);
        }
      for (const node of targets)
        if (!observed.has(node)) {
          observer?.observe(node);
          observed.add(node);
        }
      // Read every box before writing offsets. The union also works for native table rows without a wrapper.
      const boxes = nodes.map((node) => node.getBoundingClientRect());
      const scales = nodes.map((node, index) => {
        const style = getComputedStyle(node);
        const extras =
          style.boxSizing === 'border-box'
            ? 0
            : parseFloat(style.paddingLeft) +
              parseFloat(style.paddingRight) +
              parseFloat(style.borderLeftWidth) +
              parseFloat(style.borderRightWidth);
        const width = parseFloat(style.width) + extras;
        const scale = width ? boxes[index].width / width : 1;
        // CSSOM serializes fractional widths with less precision than DOMRect.
        return Math.abs(scale - 1) < 0.0001 ? 1 : scale;
      });
      const left = Math.min(...boxes.map((box) => box.left));
      const right = Math.max(...boxes.map((box) => box.right));
      nodes.forEach((node, index) => {
        const box = boxes[index];
        if (!box.width || !box.height) {
          clearGeometry(node);
          return;
        }
        const scale = scales[index];
        node.style.setProperty('--monkeys-skeleton-width', `${(right - left) / scale}px`);
        node.style.setProperty('--monkeys-skeleton-offset', `${(left - box.left) / scale}px`);
        if (!node.dataset.skeletonReady) {
          // Newly mounted shapes join the same CSS phase; geometry updates never restart it.
          node.style.setProperty('--monkeys-skeleton-delay', `${-(performance.now() % 2600)}ms`);
          node.dataset.skeletonReady = 'true';
        }
      });
    };
    const schedule = () => {
      if (active && frame === undefined) frame = window.requestAnimationFrame(measure);
    };
    const stop = () => {
      active = false;
      observer?.disconnect();
      observer = undefined;
      observed.clear();
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      frame = undefined;
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      registry.nodes.forEach((_, node) => clearGeometry(node));
    };
    const start = () => {
      stop();
      if (
        query.matches ||
        (animation === 'shimmer-shared' && typeof ResizeObserver === 'undefined')
      )
        return;
      active = true;
      if (animation === 'shimmer-shared') {
        observer = new ResizeObserver(schedule);
        window.addEventListener('resize', schedule);
        window.addEventListener('scroll', schedule, { capture: true, passive: true });
      }
      schedule();
    };
    registry.update = schedule;
    start();
    query.addEventListener('change', start);
    return () => {
      stop();
      query.removeEventListener('change', start);
      registry.update = () => {};
    };
  }, [animation, disabled, enabled, registry, containerRef]);
  // Reordering existing registered items can change their geometry without changing size.
  useEffect(() => {
    registry.update();
  });
  return registry;
}

/** A DOM-free boundary: composites retain their existing layout and measured roots. */
export function SkeletonScope({
  animation,
  animated,
  children,
  independent = false,
  containerRef,
}: {
  animation?: SkeletonGroupAnimation;
  animated?: boolean;
  children?: ReactNode;
  independent?: boolean;
  containerRef?: RefObject<HTMLElement>;
}) {
  validateSkeletonAnimation(animation, true);
  if (animated !== undefined && typeof animated !== 'boolean')
    throw new TypeError('SkeletonGroup animated must be a boolean.');
  const parent = useContext(MotionContext);
  const inheritedItem = useContext(ItemContext);
  const ownsGroup = independent || animation !== undefined || !parent;
  const mode = animation ?? parent?.animation ?? 'pulse';
  const disabled = !!parent?.disabled || animated === false || mode === 'none';
  const registry = useGroupRegistry(mode, disabled, ownsGroup, containerRef);
  const value = useMemo(
    () => ({ animation: mode, disabled, registry: ownsGroup ? registry : parent!.registry }),
    [mode, disabled, ownsGroup, registry, parent],
  );
  const content = <MotionContext.Provider value={value}>{children}</MotionContext.Provider>;
  return (
    <ItemContext.Provider value={ownsGroup ? undefined : inheritedItem}>
      {content}
    </ItemContext.Provider>
  );
}

export function SkeletonItem({ index, children }: { index: number; children?: ReactNode }) {
  const parent = useContext(ItemContext);
  return <ItemContext.Provider value={parent ?? index}>{children}</ItemContext.Provider>;
}

/** Preserve callback-ref cleanup (React 19) as well as the React 18 null callback. */
export function useSkeletonRef<T>(receive: (element: T | null) => void, forwardedRef?: Ref<T>) {
  return useMemo(() => {
    let cleanup: (() => void) | undefined;
    return (element: T | null) => {
      receive(element);
      if (typeof forwardedRef === 'function') {
        if (cleanup) {
          cleanup();
          cleanup = undefined;
        } else if (element === null) {
          forwardedRef(null);
          return;
        }
        if (element !== null) {
          const result = forwardedRef(element) as unknown;
          if (typeof result === 'function') cleanup = result as () => void;
        }
      } else if (forwardedRef) (forwardedRef as { current: T | null }).current = element;
    };
  }, [receive, forwardedRef]);
}

export function useSkeletonMotion(
  animation?: SkeletonAnimation,
  animated = true,
  forwardedRef?: Ref<HTMLDivElement>,
) {
  validateSkeletonAnimation(animation);
  const group = useContext(MotionContext);
  const item = useContext(ItemContext);
  const mode = !animated || group?.disabled ? 'none' : animation ?? group?.animation ?? 'pulse';
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const ref = useSkeletonRef(setNode, forwardedRef);
  const registry =
    animation === undefined && (mode === 'shimmer-shared' || mode === 'shimmer-stagger')
      ? group?.registry
      : undefined;
  useEffect(() => {
    if (!node || !registry) return;
    registry.nodes.set(node, { item });
    registry.update();
    return () => {
      registry.nodes.delete(node);
      clearGeometry(node);
      registry.update();
    };
  }, [node, registry, item, mode]);
  return {
    ref,
    mode,
    style:
      mode === 'shimmer-stagger' && item !== undefined
        ? ({ '--monkeys-skeleton-delay': `${Math.min(item * 80, 400)}ms` } as CSSProperties)
        : undefined,
  };
}
